import {Box} from '@dropbox/dig-foundations';
import {t} from 'i18next';
import {type PointerEvent as ReactPointerEvent, useCallback, useRef, useState} from 'react';

import styles from './AiSearchResizeHandle.module.css';

/**
 * Drag handle on the left edge of the right-anchored AI search drawer. Dragging
 * left widens the drawer, dragging right narrows it. The handle glows blue on
 * hover and while dragging to signal that it is draggable. The computed width is
 * clamped to `[minWidth, maxWidth]` so the drawer stays usable on any viewport.
 */
export const AiSearchResizeHandle = ({
  width,
  minWidth,
  maxWidth,
  onWidthChange,
  onResizeEnd,
}: {
  width: number;
  minWidth: number;
  maxWidth: number;
  onWidthChange: (width: number) => void;
  onResizeEnd?: (width: number) => void;
}) => {
  const [isDragging, setIsDragging] = useState(false);
  const frameRef = useRef<number | null>(null);

  const clamp = useCallback(
    (value: number) => Math.min(maxWidth, Math.max(minWidth, value)),
    [maxWidth, minWidth]
  );

  const onPointerDown = useCallback(
    (event: ReactPointerEvent<HTMLDivElement>) => {
      event.preventDefault();
      const handle = event.currentTarget;
      handle.setPointerCapture(event.pointerId);
      setIsDragging(true);
      let lastWidth = width;

      const onPointerMove = (moveEvent: PointerEvent) => {
        if (frameRef.current != null) {
          return;
        }
        // The drawer is anchored to the right edge, so its width is the distance
        // from the pointer to the right of the viewport.
        const next = clamp(window.innerWidth - moveEvent.clientX);
        lastWidth = next;
        frameRef.current = window.requestAnimationFrame(() => {
          frameRef.current = null;
          onWidthChange(next);
        });
      };

      const onPointerUp = () => {
        setIsDragging(false);
        if (frameRef.current != null) {
          window.cancelAnimationFrame(frameRef.current);
          frameRef.current = null;
        }
        onResizeEnd?.(lastWidth);
        handle.releasePointerCapture(event.pointerId);
        handle.removeEventListener('pointermove', onPointerMove);
        handle.removeEventListener('pointerup', onPointerUp);
        handle.removeEventListener('pointercancel', onPointerUp);
      };

      handle.addEventListener('pointermove', onPointerMove);
      handle.addEventListener('pointerup', onPointerUp);
      handle.addEventListener('pointercancel', onPointerUp);
    },
    [clamp, onResizeEnd, onWidthChange, width]
  );

  return (
    <Box
      position="absolute"
      display="flex"
      justifyContent="center"
      className={`${styles.handle}${isDragging ? ` ${styles.dragging}` : ''}`}
      onPointerDown={onPointerDown}
      role="separator"
      aria-orientation="vertical"
      aria-label={t('ai_search_resize_handle')}
      aria-valuenow={Math.round(width)}
      aria-valuemin={minWidth}
      aria-valuemax={maxWidth}
    >
      <span className={styles.grip} aria-hidden />
    </Box>
  );
};
